<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable"></report-tools>

    <report-state v-if="loading" kind="loading"></report-state>
    <report-state v-else-if="!rows.length" icon="mdi-account-cash-outline" :title="emptyTitle" :text="emptyText"></report-state>

    <ui-table
      v-else
      variant="report"
      wrap-class="rd-card--scroll"
      table-class="rd-tbl--workers"
      :columns="columns"
      :rows="rows"
      :row-key="(row) => row.key"
      :sort="sort"
      :totals="sums"
      :total-label="$t('total')"
      @sort="sortBy"
    >
      <template #cell-name="{ row }">
        <span class="rd-person">
          <span class="ui-avatar ui-avatar--neutral ui-avatar--sm">{{ row.initials }}</span>
          <span class="rd-person__name">{{ row.name }}</span>
        </span>
      </template>

      <template #cell-total="{ row }"><b>{{ money(row.total) }}</b></template>
    </ui-table>
  </div>
</template>

<script>
import CompanyMultiRestaurantTabEmployeeMealSummary from "@/components/company/reports/multi-restaurant-tabs/CompanyMultiRestaurantTabEmployeeMealSummary.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import hasTableSort from "@/components/report/desktop/hasTableSort";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import hasMultiFeed from "../../../hasMultiFeed";

const initialsOf = (name) =>
  name
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join("");

/**
 * Pregled obroka po radnicima firme "više restorana" na računaru: po radniku broj narudžbi, poručeno i preuzeto,
 * koliko je platila firma, a koliko radnik doplatio (kolona poslovnice kad ih firma ima više). Restorane,
 * poslovnice i period čita iz zajedničke trake, pretragu po imenu radnika ili poslovnici takođe. Nasleđuje učitavanje, štampu i Excel iz starog taba.
 */
export default {
  name: "CompanyDesktopMultiEmployeeMealSummary",
  extends: CompanyMultiRestaurantTabEmployeeMealSummary,
  mixins: [hasMultiFeed, hasTableSort],
  components: { ReportState, ReportTools },
  data: () => ({
    sort: { key: "name", dir: 1 },
  }),
  computed: {
    /** Kolone tabele: iste služe za zaglavlje, redove i zbirni red. */
    columns() {
      const suffix = this.currencySuffix;
      const money = { align: "end", num: true, sortable: true, format: this.money };
      return [
        { key: "#", label: "#", index: true },
        { key: "name", label: this.$t("employee"), name: true, sortable: true },
        ...(this.hasBranches
          ? [{ key: "branch", label: this.$t("branch"), muted: true, sortable: true, value: (row) => row.branch || "–", title: (row) => row.branch }]
          : []),
        { key: "orders", label: this.$t("number_of_orders"), align: "end", num: true, sortable: true },
        { key: "ordered", label: this.$t("ordered"), align: "end", num: true, sortable: true },
        { key: "taken", label: this.$t("taken"), align: "end", num: true, zero: true, sortable: true },
        { key: "company", label: `${this.$t("company_paid")}${suffix}`, zero: true, ...money },
        { key: "employee", label: `${this.$t("employee_paid")}${suffix}`, zero: true, ...money },
        { key: "total", label: `${this.$t("total")}${suffix}`, ...money },
      ];
    },
    busy() {
      return this.loading;
    },
    hasRows() {
      return this.rows.length > 0;
    },
    currencySuffix() {
      return this.majorityRestaurantCurrency ? ` (${this.majorityRestaurantCurrency})` : "";
    },
    /** Radnici posle pretrage po imenu. */
    rows() {
      const search = this.toLatin(String(this.$store.state.report.filters.search || "")).toLowerCase().trim();
      return this.sortRows(
        this.employeeMealSummary
          .filter(
            (item) =>
              !search || this.toLatin(`${item.employee_name} ${item.company_name || ""}`).toLowerCase().includes(search)
          )
          .map((item, index) => ({
            key: `${item.user_id}-${index}`,
            name: item.employee_name,
            branch: item.company_name || "",
            initials: initialsOf(item.employee_name),
            orders: item.order_count,
            ordered: item.order_quantity,
            taken: item.order_payed_quantity,
            company: item.company_paid,
            employee: item.employee_paid,
            total: item.total,
          }))
      );
    },
    sums() {
      return this.rows.reduce(
        (sums, row) => ({
          orders: sums.orders + row.orders,
          ordered: sums.ordered + row.ordered,
          taken: sums.taken + row.taken,
          company: sums.company + row.company,
          employee: sums.employee + row.employee,
          total: sums.total + row.total,
        }),
        { orders: 0, ordered: 0, taken: 0, company: 0, employee: 0, total: 0 }
      );
    },
    emptyTitle() {
      const search = this.$store.state.report.filters.search;
      return search ? this.$t("report_mobile.no_results_for", { search }) : this.$t("no_data");
    },
    emptyText() {
      return this.$store.state.report.filters.search ? "" : this.$t("select_date_and_filters");
    },
    kpis() {
      return [
        { key: "workers", label: this.$t("report_desktop.workers"), value: this.rows.length, tone: "inverse" },
        { key: "ordered", label: this.$t("ordered"), value: this.sums.ordered },
        { key: "total", label: this.$t("total"), value: this.money(this.sums.total) },
      ];
    },
  },
  methods: {
    money(value) {
      return (Number(value) || 0).toFixed(2);
    },
    async copyTable() {
      const headers = [
        this.$t("employee"),
        ...(this.hasBranches ? [this.$t("branch")] : []),
        this.$t("number_of_orders"),
        this.$t("ordered"),
        this.$t("taken"),
        this.$t("company_paid"),
        this.$t("employee_paid"),
        this.$t("total"),
      ];
      const rows = this.rows.map((row) => [
        row.name,
        ...(this.hasBranches ? [row.branch] : []),
        row.orders,
        row.ordered,
        row.taken,
        this.money(row.company),
        this.money(row.employee),
        this.money(row.total),
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
